/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import {
  TableColumnHeaderCell,
  TableHead,
  TableBody,
  TableCell,
  ScreenReaderText,
  TableExpandColumnHeaderCell,
} from '@blend/alchemy';
import React, { useContext } from 'react';

import { MeContext } from 'blb-contexts/Me';
import { BlendBridge } from 'blb-types/blendBridge';
import { formatGroupAddress } from 'blb-utils/format';
import { maxTableWidth, sidebarWidth } from 'blb-styles/constants';

import TeamsAdminActions from './TeamsAdminActions';
import {
  FullWidthTable,
  StyledRow,
  StyledExpandableRow,
  StyledRowExpand,
  TableColumnHeaderNoWrapCell,
  TableColumnHeaderFlushedRightCell,
  EllipsisCell,
} from '../styles';
import MembersTable from '../MembersTable/MembersTable';

const isCentralized = (team: BlendBridge.API.SearchGroupRecord): boolean => !!team.broadcasterPrincipalId;

/** Returns the team email for centralized teams and undefined for open sharing teams. */
const getTeamEmail = (team: BlendBridge.API.SearchGroupRecord): string | undefined => {
  const broadcaster = team.assignments.find(({ principalId }) => principalId === team.broadcasterPrincipalId);
  return broadcaster?.username;
};

const isTeamAdmin = (currentPrincipalId: string, assignments: BlendBridge.API.GroupAssignment[]): boolean =>
  assignments.some(({ principalId, role }) => principalId === currentPrincipalId && role === 'ADMIN');

interface TeamsTableProps {
  refetchTeams: () => Promise<void>;
  setTeamToEdit: React.Dispatch<React.SetStateAction<BlendBridge.API.SearchGroupRecord | null>>;
  setTeamToDelete: React.Dispatch<React.SetStateAction<BlendBridge.API.SearchGroupRecord | null>>;
  setTeamForInviteMembersModal: React.Dispatch<React.SetStateAction<any>>;
  teams: BlendBridge.API.SearchGroupRecord[];
}

const TeamsTable: React.FC<TeamsTableProps> = ({
  refetchTeams,
  setTeamToEdit,
  setTeamToDelete,
  setTeamForInviteMembersModal,
  teams,
}) => {
  const me = useContext(MeContext)!;

  const hasEmailColumn = teams.some(team => isCentralized(team));
  const hasActionsColumn = teams.some(team => isTeamAdmin(me.principalId, team.assignments));
  const expandedRowKeys = teams.length > 0 ? [teams[0].id] : [];

  return (
    <>
      <ScreenReaderText id="it-Teams-table-label">Your teams</ScreenReaderText>
      <FullWidthTable
        ariaLabelledBy="it-Teams-table-label"
        canExpandMultiple={true}
        expandedRowKeys={expandedRowKeys}
        gridTemplateColumnsRowRule={`0.8fr 1fr 11rem ${hasEmailColumn ? '1fr' : ''} ${
          hasActionsColumn ? '11rem' : ''
        } calc(100vw - ${maxTableWidth}px - ${sidebarWidth}px)`}
        isUncontrolled={true}
      >
        <TableHead>
          <StyledRow rowKey="header">
            <TableColumnHeaderCell>Team Name</TableColumnHeaderCell>
            <TableColumnHeaderCell>Address</TableColumnHeaderCell>
            <TableColumnHeaderNoWrapCell>Sharing Settings</TableColumnHeaderNoWrapCell>
            {hasEmailColumn && <TableColumnHeaderCell>Email</TableColumnHeaderCell>}
            {hasActionsColumn && <TableColumnHeaderFlushedRightCell>Actions</TableColumnHeaderFlushedRightCell>}
            <TableExpandColumnHeaderCell>Members</TableExpandColumnHeaderCell>
          </StyledRow>
        </TableHead>
        <TableBody>
          {teams.map(team => {
            const isAdmin = isTeamAdmin(me.principalId, team.assignments);
            return (
              <StyledExpandableRow id={`it-Team-${team.id}-row`} key={team.id} rowKey={team.id}>
                <EllipsisCell>{team.name}</EllipsisCell>
                <EllipsisCell>{formatGroupAddress(team.address)}</EllipsisCell>
                <TableCell>{isCentralized(team) ? 'Centralized' : 'Open'}</TableCell>
                {hasEmailColumn && <EllipsisCell>{getTeamEmail(team)}</EllipsisCell>}
                {hasActionsColumn && isAdmin && (
                  <TeamsAdminActions
                    openDeleteTeamModal={() => setTeamToDelete(team)}
                    openEditTeamInfoModal={() => setTeamToEdit(team)}
                    openInviteTeamMembersModal={() => setTeamForInviteMembersModal(team)}
                    teamId={team.id}
                  />
                )}
                <StyledRowExpand>
                  <MembersTable isTeamAdmin={isAdmin} refetchTeams={refetchTeams} team={team} />
                </StyledRowExpand>
              </StyledExpandableRow>
            );
          })}
        </TableBody>
      </FullWidthTable>
    </>
  );
};

export default TeamsTable;
